/**
 * Human-readable copy for Standard tab "Apply to <tracker>" failures.
 * For Media Devoted | Created By Ciro Autiero
 */

import { isAxiosError } from 'axios';

import {
  VOLUUM_PATH_ITEM_WEIGHT_MAX,
  VoluumPayloadValidationError,
} from '@trafficpulse/shared';

import { formatAxiosVoluumApplyFailure } from './axios-voluum-apply-error';
import type { FlowApplyFailureNotice } from './apply-notice.types';

export type { FlowApplyFailureNotice } from './apply-notice.types';

const WEIGHT_RULE = `Each lander, sublander, and offer weight must be from 0 to ${VOLUUM_PATH_ITEM_WEIGHT_MAX} (Voluum requires every such weight to be strictly less than 1000).`;

function isLikelyPathItemWeightField(path: string): boolean {
  if (!path.endsWith('.weight')) return false;
  return (
    path.includes('.landers') ||
    path.includes('.offers') ||
    path.includes('.sublanders')
  );
}

export function formatFlowApplyFailure(
  err: unknown,
  trackerName = 'Voluum',
): FlowApplyFailureNotice {
  if (err instanceof VoluumPayloadValidationError) {
    const weightHits = err.errors.filter(
      (e) =>
        e.code === 'invalid_value' &&
        isLikelyPathItemWeightField(e.path),
    );
    const lines = err.errors.map((e) => `${e.path}: ${e.message}`);
    if (weightHits.length > 0) {
      return {
        title: 'TrafficPulse blocked the save (Voluum weight limits)',
        lines: [
          WEIGHT_RULE,
          `Nothing was sent to ${trackerName} yet — fix the weights below, then apply again.`,
          ...lines.slice(0, 15),
          ...(lines.length > 15
            ? [`…and ${lines.length - 15} more field(s).`]
            : []),
        ],
      };
    }
    return {
      title: 'TrafficPulse blocked the save (payload checks)',
      lines: [
        `Your draft must pass TrafficPulse validation before we call ${trackerName}. Issues:`,
        ...lines.slice(0, 15),
        ...(lines.length > 15
          ? [`…and ${lines.length - 15} more field(s).`]
          : []),
      ],
    };
  }

  if (isAxiosError(err)) {
    return formatAxiosVoluumApplyFailure(err, WEIGHT_RULE, trackerName);
  }

  return {
    title: 'Could not apply flow',
    lines: [
      err instanceof Error
        ? err.message
        : 'An unexpected error occurred while applying.',
    ],
  };
}
